import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useState } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";

import Customer from "../components/customer/Customer";
import CustomerInfoPanel from "../components/customer/CustomerInfoPanel.comp";
import CustomerOrders from "../components/customer/CustomerOrders";
import CustomerListItem from "../components/customer/CustomerListItem";
import PosLayout from "../components/layouts/posLayout.comp";
import { useTranslation } from "../core/translation.core";
import { useCustomerInfo, usePosCustomerById, usePosCustomers } from "../hooks/services/useCustomers";
import useAdminShop from "../hooks/useAdminShop";
import useWindowSize from "../hooks/useWindowSize.hook";
import AddCustomerIcon from "../icons/addCustomer.icon";
import ChevronLeftIcon from "../icons/chevronLeft.icon";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import SearchIcon from "../icons/posicons/search.icon";
import CustomerRecycles from "../components/customer/customerRecycles";
import RecycleIcon from "../icons/recycle.icon";
import OrderListIcon from "../icons/posicons/orderList.icon";
import ProfileIcon from "../icons/profile.icon";
import ProfileInfoIcon from "../icons/profileInfo.icon";
import GiftCardIcon from "../icons/giftCard.icon";
import CustomerGiftCards from "../components/customer/CustomerGiftCards";
import { CustomerItem, CustomerListRst, CustomerListFilter } from "../types/customer";
import NewOrderIcon from "../icons/posicons/neworder.icon";
import useNavToNewOrder from "../hooks/useNavToNewOrder";
import PageNav from "../components/reuseables/pageNav.comp";

export default function PosCustomerPage() {
  const { query } = useRouter();
  const customerId = query.id;
  const isMobile = useWindowSize();

  return (
    <PosLayout title="Customer Page" noPadding>
      <div className="block lg:grid grid-cols-2 h-[calc(100vh-72.5px)]">
        <div className="overflow-auto">
          {isMobile && !customerId && <Content />}
          {!isMobile && <Content />}
        </div>

        <CustomerPanel isMobile={isMobile} />
      </div>
    </PosLayout>
  );
}

function Content(): JSX.Element {
  const { query, isReady } = useRouter();
  const methods = useForm<CustomerListFilter>({
    defaultValues: {
      page: 1,
      pageSize: 30,
      search: "",
    },
  });
  const { t, msg } = useTranslation();

  useEffect(() => {
    if (isReady) {
      methods.setValue("page", Number(query.page) || 1);
      methods.setValue("search", (query.search as string) || "");
    }
  }, [query, isReady, methods]);

  const search = useWatch({ control: methods.control, name: "search" });
  const page = useWatch({ control: methods.control, name: "page" });
  const pageSize = useWatch({ control: methods.control, name: "pageSize" });

  const { data: customerListRst, isError, isFetched, failureReason } = usePosCustomers({ search, page, pageSize });

  return (
    <main className="p-5 space-y-14 dark:text-white">
      <section className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t(msg.pos.customer)}</h1>
          <Link
            id="add-customer"
            href="/customer/add"
            className="bg-her-blue hidden lg:flex lg:items-center lg:justify-center text-white p-2 text-base rounded-md shadow-sm gap-2"
          >
            <AddCustomerIcon />
            <span>{t(msg.customer.addCustomer)}</span>
          </Link>

          <Link
            href="/customer/add"
            className="bg-her-blue flex items-center justify-center lg:hidden text-white p-2 text-base rounded-md shadow-sm flex-center gap-2"
          >
            <AddCustomerIcon />
            <span>{t(msg.customer.addCustomer)}</span>
          </Link>
        </div>

        <div className="flex gap-2 dark:text-black bg-her-lightgray lg:bg-white p-3 rounded-md w-full focus:ring-blue-700">
          <SearchIcon />

          <input
            type="text"
            {...methods.register("search", {
              onChange: () => {
                if (!search) {
                  methods.setValue("page", 1);
                }
              },
            })}
            className="outline-none bg-transparent opacity-90 text-sm w-full"
            placeholder={t(msg.customer.searchNamePhone)}
          />
        </div>
      </section>

      {customerListRst && (
        <CustomerListComponent
          names={customerListRst.list}
          isError={isError}
          isFetched={isFetched}
          failureReason={failureReason}
          methods={methods}
        />
      )}
      <Paging methods={methods} customerListRst={customerListRst} />
    </main>
  );
}

interface CustomerListProps {
  names: CustomerItem[];
  isError: boolean;
  isFetched: boolean;
  failureReason: any;
  methods: UseFormReturn<CustomerListFilter, any>;
}

function CustomerListComponent({ names, isError, isFetched, failureReason, methods }: CustomerListProps): JSX.Element {
  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <LoadingOvalCenterIcon />;

  const sortNamesAlphabetically = (names: CustomerItem[]) => {
    return names?.sort((a, b) => a.name.localeCompare(b.name));
  };

  const groupNamesByFirstLetter = (names) => {
    const groupedNames: Record<string, CustomerItem[]> = {};
    names?.forEach((person) => {
      const firstLetter = (person.lastName ?? person.firstName).charAt(0).toUpperCase();
      if (!groupedNames[firstLetter]) {
        groupedNames[firstLetter] = [];
      }
      groupedNames[firstLetter].push(person);
    });
    return groupedNames;
  };

  const sortedNames = sortNamesAlphabetically(names);
  const groupedNames = groupNamesByFirstLetter(sortedNames);

  return (
    <main className="space-y-8">
      {Object.keys(groupedNames)
        .sort()
        .map((letter: string) => (
          <section key={letter} className="space-y-4 cursor-pointer">
            <h2 className="font-bold text-her-aaa text-2xl border-b-[1px] pb-3">{letter}</h2>

            <div className="space-y-4">
              {groupedNames[letter].map((person) => (
                <CustomerListItem key={person.id} customer={person} methods={methods} />
              ))}
            </div>
          </section>
        ))}
    </main>
  );
}

type TabType = "personalInfo" | "customerDetails" | "orderDetails" | "recycles" | "giftCards";

interface CustomerTab {
  type: TabType;
  icon: ReactNode;
}

function CustomerPanel({ isMobile }: { isMobile: boolean }) {
  const { t, msg } = useTranslation();
  const { query, push } = useRouter();
  const customerId = query.id;
  const isEdit = query.edit;
  const navToNewOrder = useNavToNewOrder();

  const adminShop = useAdminShop();

  //  @ts-ignore
  const hasCustomerDetails = adminShop?.addCustomerInfo?.length > 0;
  const recycleEnabled = adminShop?.recycleEnabled;
  const [activeTab, setActiveTab] = useState<TabType>("personalInfo");
  let tabs: CustomerTab[] = [
    { type: "personalInfo", icon: <ProfileIcon className="w-8 h-8" /> },
    { type: "customerDetails", icon: <ProfileInfoIcon className="w-8 h-8" /> },
    { type: "orderDetails", icon: <OrderListIcon className="w-8 h-8" /> },
    { type: "giftCards", icon: <GiftCardIcon className="w-8 h-8" /> },
    { type: "recycles", icon: <RecycleIcon className="w-8 h-8" /> },
  ];

  if (!hasCustomerDetails) {
    tabs = tabs.filter((t) => t.type !== "customerDetails");
  }

  if (!recycleEnabled) {
    tabs = tabs.filter((t) => t.type !== "recycles");
  }

  const { data: user, isError, isPending, error } = usePosCustomerById(query.id as string);
  const { data: customerInfo } = useCustomerInfo(query.id as string);

  if (!customerId)
    return <div className="shadow-md p-6 text-2xl flex-center font-bold dark:text-white">{t(msg.customer.selectCustomer)}</div>;

  if (isError) return <ErrorCenterIcon reason={error} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!user) return null;

  function handleClick() {
    //  @ts-ignore
    push(`/customers?id=${user.id}`);
  }

  function handleNewOrder() {
    if (!user) return;
    navToNewOrder(user);
  }

  return (
    <section className="shadow-md space-y-8 px-1 lg:px-4 py-6 dark:text-white">
      <div className="flex gap-3 items-center">
        {isEdit ? (
          <button onClick={handleClick}>
            <ChevronLeftIcon className="w-8" />
          </button>
        ) : null}

        {isMobile && !isEdit && (
          <button onClick={() => push("/customers")}>
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
        )}

        <h1 className="text-base lg:text-2xl font-bold">
          {user?.lastName}&nbsp;{user?.firstName}
        </h1>

        {!isEdit && (
          <button
            onClick={handleNewOrder}
            className="text-her-green px-4 py-2 rounded-md flex items-center gap-2 text-sm lg:text-base font-medium hover:bg-her-green hover:text-white transition-colors"
          >
            <NewOrderIcon className="w-6 h-6" />
          </button>
        )}
      </div>

      {isEdit && <h1 className="text-2xl font-medium px-4">{t(msg.customer.editCustomerInfo)}</h1>}

      <div className="flex flex-col space-y-4">
        <div
          className={`relative grid gap-6 border-b-2 border-gray-400 grid-cols-3`}
          style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
        >
          {tabs.map((tab) => (
            <button
              id={`button-${tab.type}`}
              key={tab.type}
              className={`flex flex-col justify-end items-center relative py-2 px-1 lg:px-4 focus:outline-none font-semibold text-sm lg:text-base ${
                activeTab === tab.type ? "text-blue-500" : "dark:text-white text-black"
              }`}
              onClick={() => {
                setActiveTab(tab.type);
                push(`/customers?id=${customerId}`);
              }}
            >
              {tab.icon}
              <TabName tab={tab.type} />
              {activeTab === tab.type && <span className="absolute left-0 right-0 -bottom-[1.8px] h-[2px] bg-blue-500" />}
            </button>
          ))}
        </div>

        <div className="p-4">
          {activeTab === "personalInfo" && <Customer user={user} customerInfo={customerInfo} adminShop={adminShop} />}

          {activeTab === "customerDetails" && (
            <CustomerInfoPanel customer={user} customerInfo={customerInfo} adminShop={adminShop} />
          )}
          {activeTab === "orderDetails" && <CustomerOrders user={user} />}

          {activeTab === "recycles" && <CustomerRecycles user={user} />}
          {activeTab === "giftCards" && <CustomerGiftCards user={user} />}
        </div>
      </div>
    </section>
  );
}

function TabName({ tab }: { tab: TabType }) {
  const { t, msg } = useTranslation();

  switch (tab) {
    case "personalInfo":
      return <span className="md:block md:text-xs">{t(msg.customer.profile)}</span>;
    case "customerDetails":
      return <span className="md:block md:text-xs">{t(msg.customer.info)}</span>;
    case "orderDetails":
      return <span className="md:block md:text-xs">{t(msg.order.orders)}</span>;
    case "recycles":
      return <span className="md:block md:text-xs">{t(msg.recycle.recycles)}</span>;
    case "giftCards":
      return <span className="md:block md:text-xs">{t(msg.giftCard.gifts)}</span>;
    default:
      return null;
  }
}

function Paging({
  methods,
  customerListRst,
}: {
  methods: ReturnType<typeof useForm<CustomerListFilter>>;
  customerListRst?: CustomerListRst;
}) {
  const router = useRouter();
  const [pageSize] = useWatch({
    control: methods.control,
    name: ["pageSize"],
  });

  if (!customerListRst) return null;

  const currentPage = Number(router.query.page) || 1;

  return <PageNav page={currentPage} size={pageSize} total={customerListRst.size} pathname="/customers" query={router.query} />;
}
